import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { B, FONT } from '../lib/constants';

const CATEGORIES = [
  'Pre Build','Foundation','Exterior','Interior','Concrete','Windows/Doors',
  'Plumbing','Electrical','HVAC','Tile','Flooring','Paint/Stain','Finishes','General','Site'
];

const CAT_ICONS = {
  'Pre Build':'🚀', Foundation:'🏗️', Exterior:'🧱', Interior:'🛋️', Concrete:'⬜', 'Windows/Doors':'🚪',
  Plumbing:'🔧', Electrical:'⚡', HVAC:'🌡️', Tile:'🟦', Flooring:'🪵',
  'Paint/Stain':'🎨', Finishes:'✨', General:'📋', Site:'🌍',
};

export default function DecisionTemplateLibrary({ projects = [], onClose }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [sendModal, setSendModal] = useState(null);
  const [targetProject, setTargetProject] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  useEffect(() => {
    if (projects.length > 0 && !targetProject) {
      setTargetProject(projects[0].id);
    }
  }, [projects]);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from('decision_templates').select('*').order('category').order('item');
    if (data) setTemplates(data);
    setLoading(false);
  };

  const catCounts = {};
  CATEGORIES.forEach(c => { catCounts[c] = 0; });
  templates.forEach(t => { if (t.category in catCounts) catCounts[t.category]++; });

  const isSearching = search.trim().length > 0;
  const searchResults = isSearching
    ? templates.filter(t => t.item?.toLowerCase().includes(search.toLowerCase()) || (t.notes || '').toLowerCase().includes(search.toLowerCase()))
    : activeCategory ? templates.filter(t => t.category === activeCategory) : [];

  const handleSendToProject = async (template) => {
    setSending(true);
    await supabase.from('decisions').insert({
      project_id: parseInt(targetProject),
      category: template.category,
      item: template.item,
      notes: template.notes || null,
      answer_type: template.answer_type || 'text',
      options: template.options || [],
      status: 'pending',
    });
    setSending(false);
    setSent(true);
    setTimeout(() => { setSent(false); setSendModal(null); }, 1500);
  };

  const handleSendMultiple = async (templatesToSend) => {
    setSending(true);
    await supabase.from('decisions').insert(templatesToSend.map(t => ({
      project_id: parseInt(targetProject),
      category: t.category,
      item: t.item,
      notes: t.notes || null,
      answer_type: t.answer_type || 'text',
      options: t.options || [],
      status: 'pending',
    })));
    setSending(false);
    setSent(true);
    setTimeout(() => { setSent(false); }, 1500);
  };

  const handleDelete = async (id) => {
    await supabase.from('decision_templates').delete().eq('id', id);
    setTemplates(prev => prev.filter(t => t.id !== id));
  };

  const handleSaveEdit = async (t) => {
    await supabase.from('decision_templates').update({
      item: t.item, notes: t.notes, category: t.category, phase: t.phase, send_on_phase: t.send_on_phase, answer_type: t.answer_type,
    }).eq('id', t.id);
    setTemplates(prev => prev.map(x => x.id === t.id ? { ...x, ...t } : x));
    setEditingTemplate(null);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: B.black, fontFamily: FONT, display: 'flex', flexDirection: 'column', zIndex: 1000, overflowY: 'auto' }}>
      {/* Nav header — same as app */}
      <div style={{ background: B.black, borderBottom: `1px solid ${B.slate}`, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <button onClick={onClose}
          style={{ background: 'none', border: 'none', color: B.muted, cursor: 'pointer', fontSize: 22, lineHeight: 1, padding: 0, marginRight: 4 }}>⌂</button>
        {(projects || []).map(p => (
          <button key={p.id} onClick={onClose}
            style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 20, padding: '5px 12px', cursor: 'pointer', color: B.muted, fontSize: 11, fontWeight: 600, fontFamily: FONT, whiteSpace: 'nowrap' }}>
            {p.name}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button onClick={() => setShowAdd(true)}
          style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 9, padding: '7px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 800, fontFamily: FONT, flexShrink: 0 }}>
          + Add
        </button>
      </div>
      {/* Sub-header */}
      <div style={{ background: B.dark, borderBottom: `1px solid ${B.slate}`, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        {(activeCategory && !isSearching) && (
          <button onClick={() => setActiveCategory(null)}
            style={{ background: 'none', border: 'none', color: B.goldlt, cursor: 'pointer', fontSize: 14, padding: 0, fontFamily: FONT }}>‹ All</button>
        )}
        <div style={{ color: B.white, fontSize: 16, fontWeight: 800 }}>
          {activeCategory && !isSearching ? activeCategory : 'Decision Library'}
        </div>
        {activeCategory && !isSearching && (
          <div style={{ color: B.muted, fontSize: 12, marginLeft: 4 }}>({templates.filter(t => t.category === activeCategory).length})</div>
        )}
      </div>

      {/* Search */}
      <div style={{ padding: '10px 16px', background: B.dark, borderBottom: `1px solid ${B.slate}`, flexShrink: 0 }}>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: B.muted, fontSize: 14 }}>🔍</span>
          <input value={search} onChange={e => { setSearch(e.target.value); setActiveCategory(null); }}
            placeholder="Search all templates…"
            style={{ width: '100%', background: B.mid, border: `1px solid ${search ? B.gold : B.slate}`, borderRadius: 10, padding: '9px 36px', color: B.white, fontSize: 13, outline: 'none', fontFamily: FONT, boxSizing: 'border-box' }} />
          {search && <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: B.muted, fontSize: 18, cursor: 'pointer', padding: 0 }}>×</button>}
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {loading ? (
          <div style={{ color: B.muted, textAlign: 'center', padding: 40 }}>Loading…</div>
        ) : isSearching ? (
          <SearchResults results={searchResults} projects={projects} targetProject={targetProject} setTargetProject={setTargetProject}
            onSend={setSendModal} onEdit={setEditingTemplate} onDelete={handleDelete} />
        ) : !activeCategory ? (
          // Category grid
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            {CATEGORIES.map(cat => {
              const isPreBuild = cat === 'Pre Build';
              const count = catCounts[cat] || 0;
              return (
                <div key={cat} style={{ background: isPreBuild ? 'rgba(248,218,107,0.06)' : B.navy, border: `1px solid ${isPreBuild ? B.gold : B.slate}`, borderRadius: 14, overflow: 'hidden' }}>
                  <button onClick={() => setActiveCategory(cat)}
                    style={{ width: '100%', background: 'transparent', border: 'none', padding: 18, cursor: 'pointer', textAlign: 'left', fontFamily: FONT }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>{CAT_ICONS[cat] || '📋'}</div>
                    <div style={{ color: isPreBuild ? B.goldlt : B.white, fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{cat}</div>
                    <div style={{ color: B.muted, fontSize: 12 }}>{count} templates</div>
                  </button>
                  {isPreBuild && count > 0 && (
                    <div style={{ borderTop: `1px solid ${B.gold}`, padding: '10px 14px' }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <select value={targetProject} onChange={e => setTargetProject(e.target.value)}
                          style={{ flex: 1, background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 7, padding: '6px 8px', color: B.white, fontSize: 11, fontFamily: FONT }}>
                          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                        <button onClick={() => handleSendMultiple(templates.filter(t => t.category === 'Pre Build'))} disabled={sending}
                          style={{ background: sent ? '#4CAF7D' : B.goldlt, color: B.black, border: 'none', borderRadius: 7, padding: '7px 10px', cursor: 'pointer', fontSize: 11, fontWeight: 800, fontFamily: FONT, whiteSpace: 'nowrap' }}>
                          {sending ? '…' : sent ? '✓ Sent!' : '🚀 Deploy All'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          // Category detail
          <CategoryDetail
            category={activeCategory}
            templates={templates.filter(t => t.category === activeCategory)}
            projects={projects}
            targetProject={targetProject}
            setTargetProject={setTargetProject}
            onSend={setSendModal}
            onSendAll={handleSendMultiple}
            onEdit={setEditingTemplate}
            onDelete={handleDelete}
            sending={sending}
            sent={sent}
          />
        )}
      </div>

      {/* Send modal */}
      {sendModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 2000, display: 'flex', alignItems: 'flex-end' }}
          onClick={() => setSendModal(null)}>
          <div style={{ background: B.dark, borderRadius: '16px 16px 0 0', padding: 24, width: '100%', boxSizing: 'border-box' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ color: B.white, fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Send to Project</div>
            <div style={{ color: B.muted, fontSize: 13, marginBottom: 16 }}>{sendModal.item}</div>
            <select value={targetProject} onChange={e => setTargetProject(e.target.value)}
              style={{ width: '100%', background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 9, padding: '10px 12px', color: B.white, fontSize: 14, marginBottom: 14, fontFamily: FONT }}>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <button onClick={() => handleSendToProject(sendModal)} disabled={sending}
              style={{ width: '100%', background: sent ? '#4CAF7D' : B.goldlt, color: B.black, border: 'none', borderRadius: 10, padding: 14, cursor: 'pointer', fontSize: 14, fontWeight: 800, fontFamily: FONT }}>
              {sending ? 'Sending…' : sent ? '✓ Sent!' : 'Send Decision'}
            </button>
          </div>
        </div>
      )}

      {/* Add modal */}
      {showAdd && <AddEditModal onClose={() => setShowAdd(false)} onSave={async (t) => {
        const { data } = await supabase.from('decision_templates').insert([t]).select();
        if (data) setTemplates(prev => [...prev, data[0]]);
        setShowAdd(false);
      }} />}

      {/* Edit modal */}
      {editingTemplate && <AddEditModal template={editingTemplate} onClose={() => setEditingTemplate(null)} onSave={handleSaveEdit} />}
    </div>
  );
}

function CategoryDetail({ category, templates, projects, targetProject, setTargetProject, onSend, onSendAll, onEdit, onDelete, sending, sent }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ color: B.muted, fontSize: 12 }}>{templates.length} templates</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={targetProject} onChange={e => setTargetProject(e.target.value)}
            style={{ background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 8, padding: '6px 10px', color: B.white, fontSize: 12, fontFamily: FONT }}>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button onClick={() => onSendAll(templates)} disabled={sending}
            style={{ background: sent ? '#4CAF7D' : B.gold, color: '#000', border: 'none', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 800, fontFamily: FONT, whiteSpace: 'nowrap' }}>
            {sending ? '…' : sent ? '✓ Sent!' : `Send All (${templates.length})`}
          </button>
        </div>
      </div>
      {templates.map(t => (
        <div key={t.id} onClick={() => onEdit(t)}
          style={{ background: B.navy, borderRadius: 12, marginBottom: 8, border: `1px solid ${B.slate}`, overflow: 'hidden', cursor: 'pointer' }}>
          <div style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: B.white, fontSize: 14, fontWeight: 600 }}>{t.item}</div>
              {t.notes && <div style={{ color: B.muted, fontSize: 12, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.notes}</div>}
              {t.send_on_phase && <div style={{ color: B.gold, fontSize: 11, marginTop: 3 }}>⚡ {t.send_on_phase}</div>}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
              <button onClick={e => { e.stopPropagation(); onSend(t); }}
                style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 7, padding: '6px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 800, fontFamily: FONT }}>
                Send
              </button>
              <button onClick={e => { e.stopPropagation(); onDelete(t.id); }}
                style={{ background: 'transparent', border: '1px solid #E74C3C', borderRadius: 7, padding: '6px 10px', cursor: 'pointer', color: '#E74C3C', fontSize: 11, fontFamily: FONT }}>
                ✕
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SearchResults({ results, projects, targetProject, setTargetProject, onSend, onEdit, onDelete }) {
  if (results.length === 0) return <div style={{ color: B.muted, textAlign: 'center', padding: 32 }}>No templates found</div>;
  return (
    <div>
      <div style={{ color: B.muted, fontSize: 12, marginBottom: 12 }}>{results.length} results</div>
      {results.map(t => (
        <div key={t.id} onClick={() => onEdit(t)}
          style={{ background: B.navy, borderRadius: 12, padding: '14px 16px', marginBottom: 8, border: `1px solid ${B.slate}`, cursor: 'pointer' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: B.goldlt, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', marginBottom: 3 }}>{t.category}</div>
              <div style={{ color: B.white, fontSize: 14, fontWeight: 600 }}>{t.item}</div>
              {t.notes && <div style={{ color: B.muted, fontSize: 12, marginTop: 3 }}>{t.notes}</div>}
              {t.send_on_phase && <div style={{ color: B.gold, fontSize: 11, marginTop: 3 }}>⚡ {t.send_on_phase}</div>}
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
              <button onClick={e => { e.stopPropagation(); onSend(t); }}
                style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 7, padding: '6px 12px', cursor: 'pointer', fontSize: 11, fontWeight: 800, fontFamily: FONT }}>
                Send
              </button>
              <button onClick={e => { e.stopPropagation(); onDelete(t.id); }}
                style={{ background: 'transparent', border: '1px solid #E74C3C', borderRadius: 7, padding: '6px 10px', cursor: 'pointer', color: '#E74C3C', fontSize: 11, fontFamily: FONT }}>
                ✕
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function AddEditModal({ template, onClose, onSave }) {
  const isEdit = !!template;
  const [form, setForm] = useState(template || { category: 'Pre Build', item: '', notes: '', phase: '', send_on_phase: '', answer_type: 'text', options: [] });
  const [saving, setSaving] = useState(false);
  const [optionInput, setOptionInput] = useState('');
  const optionInputRef = useRef(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addOption = () => {
    const val = optionInput.trim();
    if (!val) return;
    set('options', [...(form.options || []), val]);
    setOptionInput('');
    setTimeout(() => optionInputRef.current?.focus(), 50);
  };
  const removeOption = (i) => set('options', (form.options || []).filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (!form.item.trim()) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 2000, display: 'flex', alignItems: 'flex-end' }}
      onClick={onClose}>
      <div style={{ background: B.dark, borderRadius: '20px 20px 0 0', padding: 24, width: '100%', maxHeight: '92vh', overflowY: 'auto', boxSizing: 'border-box' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ color: B.white, fontSize: 18, fontWeight: 800 }}>{isEdit ? 'Edit Template' : 'New Template'}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: B.muted, fontSize: 24, cursor: 'pointer', padding: 0 }}>×</button>
        </div>

        {/* Category */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: B.muted, fontSize: 10, marginBottom: 6, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Category</div>
          <select value={form.category} onChange={e => set('category', e.target.value)}
            style={{ width: '100%', background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 10, padding: '11px 12px', color: B.white, fontSize: 14, fontFamily: FONT }}>
            {['Pre Build','Foundation','Exterior','Interior','Concrete','Windows/Doors','Plumbing','Electrical','HVAC','Tile','Flooring','Paint/Stain','Finishes','General','Site'].map(c =>
              <option key={c} value={c}>{c}</option>
            )}
          </select>
        </div>

        {/* Decision item */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: B.muted, fontSize: 10, marginBottom: 6, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Decision *</div>
          <input value={form.item} onChange={e => set('item', e.target.value)} placeholder="e.g. Countertop material selection"
            style={{ width: '100%', background: B.mid, border: `1px solid ${form.item ? B.gold : B.slate}`, borderRadius: 10, padding: '11px 12px', color: B.white, fontSize: 14, outline: 'none', fontFamily: FONT, boxSizing: 'border-box' }} />
        </div>

        {/* Notes */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: B.muted, fontSize: 10, marginBottom: 6, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Notes / Context for Client</div>
          <textarea value={form.notes || ''} onChange={e => set('notes', e.target.value)} rows={3}
            placeholder="Describe what they need to decide, links, examples…"
            style={{ width: '100%', background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 10, padding: '11px 12px', color: B.white, fontSize: 14, outline: 'none', fontFamily: FONT, resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5 }} />
        </div>

        {/* Answer type */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: B.muted, fontSize: 10, marginBottom: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Answer Type</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {['text', 'options', 'yesno'].map(t => (
              <button key={t} onClick={() => set('answer_type', t)}
                style={{ flex: 1, background: form.answer_type === t ? B.goldlt : B.mid, color: form.answer_type === t ? B.black : B.muted, border: `1px solid ${form.answer_type === t ? B.gold : B.slate}`, borderRadius: 8, padding: '9px 6px', cursor: 'pointer', fontSize: 12, fontWeight: 700, textTransform: 'capitalize', fontFamily: FONT }}>
                {t === 'yesno' ? 'Yes / No' : t === 'text' ? 'Free Text' : 'Options'}
              </button>
            ))}
          </div>
        </div>

        {/* Options list */}
        {form.answer_type === 'options' && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ color: B.muted, fontSize: 10, marginBottom: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Options</div>
            {(form.options || []).map((opt, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <div style={{ flex: 1, background: B.navy, borderRadius: 8, padding: '8px 12px', color: B.white, fontSize: 13 }}>{opt}</div>
                <button onClick={() => removeOption(i)} style={{ background: 'none', border: 'none', color: '#E74C3C', fontSize: 18, cursor: 'pointer', padding: '0 4px' }}>×</button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <input ref={optionInputRef} value={optionInput} onChange={e => setOptionInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addOption()}
                placeholder="Add option…"
                style={{ flex: 1, background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 8, padding: '9px 12px', color: B.white, fontSize: 13, outline: 'none', fontFamily: FONT }} />
              <button onClick={addOption}
                style={{ background: B.slate, color: B.white, border: 'none', borderRadius: 8, padding: '9px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: FONT }}>+ Add</button>
            </div>
          </div>
        )}

        {/* Yes/No preview */}
        {form.answer_type === 'yesno' && (
          <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
            <div style={{ flex: 1, background: B.navy, border: `1px solid ${B.slate}`, borderRadius: 8, padding: '10px', textAlign: 'center', color: '#4CAF7D', fontSize: 13, fontWeight: 700 }}>✓ Yes</div>
            <div style={{ flex: 1, background: B.navy, border: `1px solid ${B.slate}`, borderRadius: 8, padding: '10px', textAlign: 'center', color: '#E74C3C', fontSize: 13, fontWeight: 700 }}>✕ No</div>
          </div>
        )}

        {/* Auto-send phase */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ color: B.muted, fontSize: 10, marginBottom: 6, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>Auto-Send on Phase</div>
          <select value={form.send_on_phase || ''} onChange={e => set('send_on_phase', e.target.value)}
            style={{ width: '100%', background: B.mid, border: `1px solid ${form.send_on_phase ? B.gold : B.slate}`, borderRadius: 10, padding: '11px 12px', color: form.send_on_phase ? B.white : B.muted, fontSize: 14, fontFamily: FONT }}>
            <option value=''>— None —</option>
            <option value='Pre Build'>Pre Build</option>
            <option value='Site Prep Excavation'>Site Prep Excavation</option>
            <option value='Form Footings/Rebar Schedule'>Form Footings/Rebar Schedule</option>
            <option value='Pour Footings'>Pour Footings</option>
            <option value='Block Foundation'>Block Foundation</option>
            <option value='Framing'>Framing</option>
            <option value='Windows/Doors'>Windows/Doors</option>
            <option value='Roofing'>Roofing</option>
            <option value='Plumbing Rough In'>Plumbing Rough In</option>
            <option value='HVAC Rough In'>HVAC Rough In</option>
            <option value='Electrical Rough In'>Electrical Rough In</option>
            <option value='Insulation'>Insulation</option>
            <option value='Drywall'>Drywall</option>
            <option value='Tile'>Tile</option>
            <option value='Hardwood Floors'>Hardwood Floors</option>
            <option value='Trim'>Trim</option>
            <option value='Cabinets'>Cabinets</option>
            <option value='Countertops'>Countertops</option>
            <option value='Paint'>Paint</option>
            <option value='Fixtures/Finish Plumbing'>Fixtures/Finish Plumbing</option>
            <option value='Finish Electrical'>Finish Electrical</option>
            <option value='Landscaping'>Landscaping</option>
            <option value='Final Walkthrough'>Final Walkthrough</option>
          </select>
        </div>

        <button onClick={handleSave} disabled={saving || !form.item.trim()}
          style={{ width: '100%', background: form.item.trim() ? B.goldlt : B.slate, color: form.item.trim() ? B.black : B.muted, border: 'none', borderRadius: 12, padding: 16, cursor: form.item.trim() ? 'pointer' : 'default', fontSize: 15, fontWeight: 800, fontFamily: FONT, marginBottom: 10 }}>
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Template'}
        </button>
        <button onClick={onClose}
          style={{ width: '100%', background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 12, padding: 14, cursor: 'pointer', color: B.muted, fontSize: 14, fontFamily: FONT }}>
          Cancel
        </button>
      </div>
    </div>
  );
}
